import React, { useCallback, useMemo } from "react"
import {
  Box, Flex, FormControl, FormHelperText, FormLabel, Text,
} from "@chakra-ui/react"
import { Control, FieldValues, useController } from "react-hook-form"
import { LexicalComposer } from "@lexical/react/LexicalComposer"
import { PlainTextPlugin } from "@lexical/react/LexicalPlainTextPlugin"
import { ContentEditable } from "@lexical/react/LexicalContentEditable"
import { HistoryPlugin } from "@lexical/react/LexicalHistoryPlugin"
import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext"
import { LexicalErrorBoundary } from "@lexical/react/LexicalErrorBoundary"
import { $getSelection, $isRangeSelection } from "lexical"
import { DioboxEvent } from "sharedTypes"
import {
  $createMergeTagNode,
  createMergeTagEditorConfig,
  DragDropPlugin,
  EMPTY_MERGE_TAG_FORMS,
  findLabel,
  MergeFieldInsertPopover,
  MergeTagEditorContext,
  MergeTagInitPlugin,
  MergeTagKeyboardPlugin,
  MergeTagOnChangePlugin,
  SingleLinePlugin,
} from "components/MergeTagLexical"

type Props<T extends FieldValues = FieldValues> = {
  name: string
  control: Control<T>
  event: DioboxEvent
  label?: string
  labelSize?: string
  helperText?: string
}

const InsertFieldButton = ({ event }: { event: DioboxEvent }) => {
  const [editor] = useLexicalComposerContext()

  const insertTag = (tagId: string) => {
    const label = findLabel(tagId, EMPTY_MERGE_TAG_FORMS)
    editor.update(() => {
      const selection = $getSelection()
      if ($isRangeSelection(selection)) {
        selection.insertNodes([$createMergeTagNode(tagId, label)])
      }
    })
    editor.focus()
  }

  return (
    <MergeFieldInsertPopover
      event={event}
      draggable
      guestFieldsOnly
      subjectLineOnly
      iconTrigger
      onInsert={insertTag}
    />
  )
}

const SubjectLineEditor = <T extends FieldValues, >({
  name, control, event, label, labelSize = "md", helperText,
}: Props<T>) => {
  const { field } = useController<any>({ name, control })
  const initialConfig = useMemo(() => createMergeTagEditorConfig("SubjectLineEditor"), [])

  const handleChange = useCallback((plainText: string) => {
    field.onChange(plainText)
  }, [field])

  return (
    <FormControl display="flex" alignItems="flex-start" flexDirection="row" mb={4}>
      {label && (
        <FormLabel
          w={1 / 4}
          fontSize={labelSize}
          mb={0}
          minH={10}
          display="flex"
          alignItems="center"
        >
          {label}
        </FormLabel>
      )}
      <Box w="100%">
        <MergeTagEditorContext.Provider value={{ groupedForms: [], subjectLineMode: true }}>
          <Box
            borderWidth={1}
            borderColor="gray.200"
            borderRadius="md"
            bg="white"
          >
            <LexicalComposer initialConfig={initialConfig}>
              <Flex align="center" minH={9} px={2} gap={2}>
                <Box flex={1} minW={0} position="relative" overflow="hidden" isolation="isolate">
                  <PlainTextPlugin
                    contentEditable={(
                      <ContentEditable
                        data-testid="subject-line-editor"
                        aria-label={label ?? "Subject line"}
                        onBlur={field.onBlur}
                        style={{
                          outline: "none",
                          fontSize: "1rem",
                          lineHeight: "2rem",
                          boxSizing: "border-box",
                          minHeight: "3rem",
                          padding: "0.5rem",
                          whiteSpace: "nowrap",
                          overflowX: "auto",
                          overflowY: "hidden",
                          fontFamily: "inherit",
                          position: "relative",
                        }}
                      />
                    )}
                    placeholder={(
                      <Text
                        color="gray.400"
                        position="absolute"
                        top="50%"
                        transform="translateY(-50%)"
                        left="0.5rem"
                        pointerEvents="none"
                        fontSize="md"
                        whiteSpace="nowrap"
                      >
                        Enter subject line...
                      </Text>
                    )}
                    ErrorBoundary={LexicalErrorBoundary}
                  />
                </Box>
                <Box flexShrink={0}>
                  <InsertFieldButton event={event} />
                </Box>
              </Flex>
              <HistoryPlugin />
              <MergeTagOnChangePlugin onChange={handleChange} encodeForSms={false} />
              <DragDropPlugin />
              <MergeTagKeyboardPlugin />
              <SingleLinePlugin />
              <MergeTagInitPlugin fieldValue={field.value ?? ""} forms={EMPTY_MERGE_TAG_FORMS} />
            </LexicalComposer>
          </Box>
        </MergeTagEditorContext.Provider>
        {helperText && (
          <FormHelperText whiteSpace="pre-line">{helperText}</FormHelperText>
        )}
      </Box>
    </FormControl>
  )
}

export default SubjectLineEditor
